<template>
    <div class="page-content">
        <div class="container has-text-left">
            <div class="section">
                <h1 class="title is-2 has-text-grey">
                    {{ $t('checkout.finaliseOrder.thankYouForOrdering') }}
                </h1>
                <p
                    class="title is-3 has-text-grey"
                    v-html="
                        $t('checkout.finaliseOrder.paymentConfirmationSentTo', {
                            email: orderTrackingInfo
                                ? orderShippingAddress.customerEmail.value
                                : paymentDetails['e_mail']
                                ? paymentDetails['e_mail']
                                : 'Sie',
                        })
                    "
                ></p>
            </div>
        </div>
        <div class="container" v-if="orderTrackingInfo">
            <div class="section">
                <div class="columns">
                    <div class="column is-full-tablet">
                        <div class="order-sum summary-details">
                            <h3 class="title is-4 has-text-grey">
                                {{ $t('orderDetails.labels.items') }}
                            </h3>
                            <div class="main-cart p-0">
                                <cart-item-list
                                    :cart-items="orderTrackingInfo.items"
                                    :is-editable="false"
                                    has-minimal-items
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="has-background-grey" />
            <div class="section">
                <h3 class="title is-4 has-text-grey mb-1">
                    Details zur Bestellung
                </h3>
                <div class="columns is-gapless">
                    <div class="column is-half-tablet summary-details">
                        <div class="detail__list">
                            <div
                                v-for="detailEntry in orderDetails"
                                :key="`order-sum-entry__${detailEntry.label}`"
                                class="detail__entry order-sum__entry"
                            >
                                <div class="entry__label">
                                    {{ detailEntry.label }}
                                </div>
                                <div class="entry__value">
                                    {{ detailEntry.value }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section">
                <div class="columns is-gapless">
                    <div class="column">
                        <hr class="has-background-grey" />
                        <h3
                            class="detail__header title is-4 is-uppercase has-text-grey"
                        >
                            Lieferadresse
                        </h3>
                        <div class="summary-details">
                            <div class="detail__list">
                                <div
                                    v-for="detailEntry in orderShippingAddress"
                                    :key="`order-summary__${detailEntry.label}`"
                                    class="detail__entry order-sum__entry"
                                >
                                    <div class="entry__label">
                                        {{ detailEntry.label }}
                                    </div>
                                    <div class="entry__value">
                                        {{ detailEntry.value }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="column">
                        <hr class="has-background-grey" />
                        <h3
                            class="detail__header title is-4 is-uppercase has-text-grey"
                        >
                            Rechnungsadresse
                        </h3>
                        <div class="summary-details">
                            <div class="detail__list">
                                <div
                                    v-for="detailEntry in orderBillingAddress"
                                    :key="`order-billingAddress__${detailEntry.label}`"
                                    class="detail__entry order-sum__entry"
                                >
                                    <div class="entry__label">
                                        {{ detailEntry.label }}
                                    </div>
                                    <div class="entry__value">
                                        {{ detailEntry.value }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="has-background-grey" />
            <div class="section">
                <div class="columns">
                    <div class="column is-half-tablet">
                        <div class="order-sum summary-details">
                            <h3 class="title is-4 has-text-grey">
                                ZUSAMMENFASSUNG
                            </h3>
                            <div class="detail__list">
                                <div class="detail__entry">
                                    <span class="order-sum__label">{{
                                        orderPaymentDetails.paymentMethod.label
                                    }}</span
                                    ><span class="order-sum__value">{{
                                        orderPaymentDetails.paymentMethod.value
                                    }}</span>
                                </div>
                                <div class="detail__entry">
                                    <span class="order-sum__label">{{
                                        orderDetails.productionTime.label
                                    }}</span
                                    ><span class="order-sum__value">{{
                                        $tc(
                                            'checkout.productionTimeDays',
                                            orderTrackingInfo.estimatedProductionTimeDays
                                        )
                                    }}</span>
                                </div>
                                <div class="detail__entry">
                                    <span class="order-sum__label"
                                        >Voraussichtl. Versandzeit</span
                                    ><span class="order-sum__value">{{
                                        $tc(
                                            'checkout.productionTimeDays',
                                            orderTrackingInfo.shippingMethod
                                                .shippingTimeDays
                                        )
                                    }}</span>
                                </div>
                                <div class="detail__entry">
                                    <span class="order-sum__label"
                                        >Voraussichtl. Gesamtlieferzeit</span
                                    ><strong class="order-sum__value">
                                        {{
                                            $tc(
                                                'checkout.productionTimeDays',
                                                orderTrackingInfo.shippingMethod
                                                    .shippingTimeDays +
                                                    orderTrackingInfo.estimatedProductionTimeDays
                                            )
                                        }}</strong
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section">
                <div class="order-sum summary-details">
                    <div class="detail__list">
                        <div class="detail__entry order-sum__order-sum">
                            <div class="order-sum__label">
                                {{ orderPaymentDetails.orderSum.label }}
                            </div>
                            <div class="order-sum__value">
                                {{
                                    orderPaymentDetails.orderSum.value
                                        | toCents
                                        | currency
                                }}
                            </div>
                        </div>
                        <div class="detail__entry order-sum__order-sum">
                            <div class="order-sum__label">
                                {{ orderPaymentDetails.shippingPrice.label }}
                            </div>
                            <div class="order-sum__value">
                                {{ orderPaymentDetails.shippingPrice.value }}
                            </div>
                        </div>
                        <div
                            class="detail__entry order-sum__entry--primary has-background-primary-dark has-text-light section mt-2"
                        >
                            <div class="order-sum__label">
                                <span class="title is-4 has-text-white">{{
                                    orderPaymentDetails.sum.label
                                }}</span>
                                <span>{{
                                    $t('checkout.cart.includingVAT')
                                }}</span>
                            </div>
                            <strong
                                class="order-sum__value title is-4 has-text-white"
                            >
                                {{
                                    orderPaymentDetails.sum.value
                                        | toCents
                                        | currency
                                }}
                            </strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="container">
            <div class="section">
                <a @click="goToStart()" class="is-size-3"
                    >Zurück zur Startseite</a
                >
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CartItemList from './CartItemList';
import { orderProcess } from '../../store/constants/order-process';
import AddressSummary from './ReviewOrder/AdressSummary';

export default {
    name: 'OrderComplete',
    components: { AddressSummary, CartItemList },
    computed: {
        ...mapGetters(['orderTrackingInfo', 'paymentDetails', 'taxRate']),
        orderDetails() {
            return {
                orderID: {
                    label: 'Auftragsnummer',
                    value: this.orderTrackingInfo.orderNumber,
                },
                orderDate: {
                    label: 'Bestelldatum',
                    value: this.$i18n.d(
                        new Date(this.orderTrackingInfo.createdAt)
                    ),
                },
                orderState: {
                    label: 'Bestellstatus',
                    value: this.$i18n.t(
                        `checkout.orderStatusLabels.${this.orderTrackingInfo.orderStatus}`
                    ),
                },
                productionTime: {
                    label: 'Produktionszeit',
                    value: this.$i18n.tc(
                        'checkout.productionTimeDays',
                        this.orderTrackingInfo.estimatedProductionTimeDays
                    ),
                },
                shippingTime: {
                    label: '+ voraussichtlich Versandzeit',
                    value: this.$i18n.tc(
                        'checkout.productionTimeDays',
                        this.orderTrackingInfo.shippingMethod.shippingTimeDays
                    ),
                },
            };
        },
        orderShippingAddress() {
            return {
                customerName: {
                    label: 'Name',
                    value: `${this.orderTrackingInfo.shippingInformation.firstname} ${this.orderTrackingInfo.shippingInformation.lastname}`,
                },
                customerEmail: {
                    label: 'E-Mail',
                    value: this.orderTrackingInfo.email,
                },
                customerStreet: {
                    label: 'Addresse',
                    value: `${this.orderTrackingInfo.shippingInformation.street}`,
                },
                customerZipAndCity: {
                    label: 'Ort',
                    value: `${this.orderTrackingInfo.shippingInformation.postalCode} ${this.orderTrackingInfo.shippingInformation.city}`,
                },
                customerCountry: {
                    label: 'Land',
                    value: `${this.orderTrackingInfo.shippingInformation.country.name}`,
                },
            };
        },
        orderBillingAddress() {
            return {
                customerName: {
                    label: 'Name',
                    value: `${this.orderTrackingInfo.billingInformation.firstname} ${this.orderTrackingInfo.billingInformation.lastname}`,
                },
                customerEmail: {
                    label: 'E-Mail',
                    value: this.orderTrackingInfo.email,
                },
                customerStreet: {
                    label: 'Addresse',
                    value: `${this.orderTrackingInfo.billingInformation.street}`,
                },
                customerZipAndCity: {
                    label: 'Ort',
                    value: `${this.orderTrackingInfo.billingInformation.postalCode} ${this.orderTrackingInfo.billingInformation.city}`,
                },
                customerCountry: {
                    label: 'Land',
                    value: `${this.orderTrackingInfo.billingInformation.country.name}`,
                },
            };
        },
        orderPaymentDetails() {
            return {
                orderSum: {
                    label: 'Gesamtpreis (ohne Versand)',
                    value: `${(
                        (this.orderTrackingInfo.positionPrice *
                            (100 + this.taxRate)) /
                        100
                    ).toFixed(2)}`,
                },
                shippingPrice: {
                    label: 'Versandkosten',
                    value: `${(
                        (this.orderTrackingInfo.shippingTotal *
                            (100 + this.taxRate)) /
                        100
                    ).toFixed(2)}`,
                },
                sum: {
                    label: 'Zahlungsbetrag Gesamt',
                    value: `${parseFloat(
                        this.orderTrackingInfo.amountTotal
                    ).toFixed(2)}`,
                },
                paymentMethod: {
                    label: 'Bezahlung',
                    value: this.$i18n.t(
                        `checkout.paymentMethodLabels.${this.orderTrackingInfo.billingInformation.paymentMethod}`
                    ),
                },
            };
        },
    },
    methods: {
        goToStart() {
            this.$router.push('/');
        },
        fetchCMSData() {
            this.$store.dispatch('fetchCMSPage', { urlSlug: '/' });
        },
    },
    created() {
        let cookieConsent = localStorage.getItem('cookies');
        let mrSessionId = localStorage.getItem('mrSessionId');
        let userPreferences = localStorage.getItem('userPreferences');
        localStorage.clear();
        localStorage.setItem('cookies', cookieConsent);
        localStorage.setItem('mrSessionId', mrSessionId);
        localStorage.setItem('userPreferences', userPreferences);
        this.fetchCMSData();
        this.$store.dispatch(
            'setCurrentOrderProcessStep',
            orderProcess.STEPS.ORDER_COMPLETED
        );
        this.$store.dispatch('clearCart');
        this.$store.dispatch('discardDiscount');
        this.$store.dispatch('createCart');

        this.$store.dispatch('fetchOrderTrackingInfo', {
            orderId: this.$route.params.orderId,
        });

        if (this.$route.query.token && this.$route.query.PayerID) {
            this.$store.dispatch('handlePayPalCallback', {
                ...this.$route.query,
                success: true,
            });
        }
    },
};
</script>

<style scoped></style>
